import { FileText } from "lucide-react";
import { SEOHead } from "../components/SEOHead";

const SECTIONS = [
  {
    title: "1. About These Terms",
    content: `These Terms of Service ("Terms") govern your use of the No Fuss Websites website and the purchase of web design and development services from No Fuss Websites (ABN: 57 371 480 477), Melbourne, Victoria, Australia ("we", "us", "our").

By using our website or purchasing our services, you agree to be bound by these Terms. If you do not agree, please do not use our services.`
  },
  {
    title: "2. Our Services",
    content: `No Fuss Websites provides web design, development, and digital services including but not limited to:
• Starter, Business, and eCommerce website packages
• Domain registration and website hosting
• SEO setup and optimisation
• Website maintenance and support

All services are subject to availability and our acceptance of your order. We reserve the right to refuse service to anyone for any reason.`
  },
  {
    title: "3. Pricing & Payment",
    content: `All prices are listed in Australian Dollars (AUD) and include GST unless otherwise stated.

• Website packages are one-time payments unless a recurring hosting or maintenance plan is selected
• Payment is required upfront via our secure checkout (powered by Stripe)
• Hosting and maintenance plans are billed monthly or annually as agreed
• Prices are subject to change. We will honour the price at the time of your confirmed purchase`
  },
  {
    title: "4. Money-Back Guarantee",
    content: `We stand behind our work with two refund policies:

Change of Mind — 7 Days
If you change your mind after purchasing, you may request a full refund within 7 days of payment, provided the project has not been completed and delivered. To request a change-of-mind refund, notify us in writing at hello@nofusswebsites.com.au within 7 days of your purchase date.

Fault / Not as Expected — 60 Days
If your completed website does not function as described, contains significant errors, or fails to meet the agreed specifications, you are entitled to a full refund within 60 days of delivery. This includes situations where:
• The website has major technical faults or broken functionality
• The delivered website materially differs from what was agreed
• Core features outlined in your package are missing or non-functional

To make a fault-based claim, contact us at hello@nofusswebsites.com.au with a description of the issue. We will first attempt to resolve the issue. If we are unable to do so within 10 business days, a full refund will be issued.

Exclusions
Refunds are not available for:
• Domain registration or third-party hosting fees (non-refundable from our providers)
• Change-of-mind requests made after 7 days of purchase
• Issues caused by client-provided content, third-party plugins, or changes made by the client after delivery

Nothing in these terms limits your rights under the Australian Consumer Law.`
  },
  {
    title: "5. Project Delivery",
    content: `We aim to deliver completed websites within 48 hours of receiving all required content and approvals from you. Delivery timelines depend on:
• Your prompt provision of content, images, and feedback
• Timely responses to our requests for approval

Delays caused by the client's failure to provide information or approvals in a reasonable time are not our responsibility. We will communicate realistic timelines if your project requires additional time.`
  },
  {
    title: "6. Client Responsibilities",
    content: `You agree to:
• Provide accurate and complete information required for your project
• Ensure you own or have the right to use any content, images, or materials you provide to us
• Respond to feedback and approval requests within 5 business days
• Not use our services for any unlawful, deceptive, or harmful purpose

You are responsible for reviewing and approving the final website before launch.`
  },
  {
    title: "7. Intellectual Property",
    content: `Upon receipt of full payment, all custom design and code created for your project becomes your property. 

We retain the right to:
• Display your completed website in our portfolio and marketing materials (with your permission)
• Reuse non-proprietary code components, templates, and design elements in other projects

All third-party assets (fonts, stock images, plugins) remain subject to their respective licences.`
  },
  {
    title: "8. Hosting & Domain Services",
    content: `If you purchase hosting or domain services through us:
• Hosting plans are provided on a month-to-month or annual basis and renew automatically unless cancelled
• Domain registrations are managed on your behalf and renew annually
• We are not liable for downtime caused by third-party hosting providers
• You may cancel hosting at any time with 30 days written notice; prepaid periods are non-refundable`
  },
  {
    title: "9. Limitation of Liability",
    content: `To the maximum extent permitted by Australian law, No Fuss Websites is not liable for any indirect, incidental, special, or consequential damages arising from your use of our services, including loss of revenue, data, or business opportunities.

Our total liability to you for any claim arising from our services is limited to the amount you paid us for the specific service giving rise to the claim.`
  },
  {
    title: "10. Warranty Disclaimer",
    content: `We warrant that our services will be provided with due care and skill. We do not warrant that:
• Your website will achieve specific search engine rankings or business outcomes
• Our services will be error-free or uninterrupted
• Third-party services (hosting, payment processors) will always be available

Nothing in these Terms excludes or limits any rights you have under the Australian Consumer Law.`
  },
  {
    title: "11. Termination",
    content: `Either party may terminate a service agreement with 30 days written notice. We may terminate immediately if you breach these Terms or engage in conduct that is harmful, illegal, or disruptive.

Upon termination, you will receive all work completed up to the termination date. Outstanding invoices remain payable.`
  },
  {
    title: "12. Governing Law",
    content: `These Terms are governed by the laws of Victoria, Australia. Any disputes will be subject to the exclusive jurisdiction of the courts of Victoria.

If any provision of these Terms is found to be unenforceable, the remaining provisions will continue to apply.`
  },
  {
    title: "13. Changes to These Terms",
    content: `We may update these Terms from time to time. Continued use of our services after changes constitutes acceptance of the updated Terms. We will update the "Last updated" date when changes are made.`
  },
  {
    title: "14. Contact",
    content: `For questions about these Terms, please contact us:

Email: hello@nofusswebsites.com.au
Phone: 0466 476 264
Address: Melbourne, Victoria, Australia`
  }
];

export default function TermsOfServicePage() {
  return (
    <main className="pt-16" data-testid="terms-of-service-page">
      <SEOHead title="Terms of Service" description="No Fuss Websites terms and conditions. Read our terms of service for website design, hosting, and related services." path="/terms-of-service" />
      {/* Hero */}
      <section className="bg-black text-white border-b-4 border-[#FFD600] py-16">
        <div className="container-main">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FFD600] rounded-xl flex items-center justify-center">
              <FileText size={20} className="text-black" />
            </div>
            <span className="text-[#FFD600] font-bold uppercase tracking-widest text-sm">Legal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Terms of Service
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Please read these terms carefully before using our services. They outline your rights and obligations when working with No Fuss Websites.
          </p>
          <p className="text-gray-500 text-sm mt-4">Last updated: February 2026</p>
        </div>
      </section>

      {/* Quick summary banner */}
      <section className="bg-[#FFD600] border-b-4 border-black py-6">
        <div className="container-main max-w-4xl">
          <p className="font-bold text-black text-center text-sm md:text-base">
            <strong>Plain English summary:</strong> Pay us, we build your website fast, you own it. Changed your mind? 7-day refund. Website faulty or not as expected? 60-day refund. Simple.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container-main max-w-4xl">
          <div className="space-y-10">
            {SECTIONS.map((s) => (
              <div key={s.title} className="border-l-4 border-[#FFD600] pl-6">
                <h2 className="text-xl font-black text-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{s.title}</h2>
                <div className="text-gray-700 leading-relaxed whitespace-pre-line text-sm md:text-base">{s.content}</div>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-16 p-6 bg-[#F4F4F5] border-2 border-black rounded-2xl">
            <p className="text-sm text-gray-600 text-center">
              These Terms of Service apply to No Fuss Websites (ABN: 57 371 480 477), Melbourne, Victoria, Australia.
              For questions, email <a href="mailto:hello@nofusswebsites.com.au" className="text-[#0055FF] font-semibold hover:underline">hello@nofusswebsites.com.au</a>.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
